'use client'

import { motion } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'
import { useLocale } from 'next-intl'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import { ICurrency } from '@/api/cities/types/cities-types'
import { FadeOverlay } from '@/components/fade-overlay'
import { LoadingSpinner } from '@/components/loading-spinner'
import { SeparationLine } from '@/components/separation-line'
import { RestaurantCardDeals } from '@/features/restaurants/restaurant-card/restaurant-card-deals'
import { useRestaurantCardsContext } from '@/features/restaurants/restaurant-card/restaurant-cards-context'
import { RestaurantPriceRange } from '@/features/restaurants/restaurant-price-range'
import { RestaurantTags } from '@/features/restaurants/restaurant-tags'
import { Rating } from '@/features/reviews/rating'
import { Locale } from '@/setup/locales'
import { getLinkHref } from '@/setup/routing'
import { IOnClickEvent } from '@/typings/onclick-event'
import { createImageUrl } from '@/utils/image/create-image-url'
import { usePreloadImage } from '@/utils/image/use-preload-image'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'

export interface IRestaurantCardRestaurant {
    images?: Array<{ url: string }> | null
    name: string
    avgRating: number
    label?: string
    tags?: Array<{ name: string; slug?: string; category?: string; emoji?: string }> | null
    ratingsCount: number
    priceRange: number
    slug: string
    deals?: Array<{ name: string | null; eventDeal?: boolean | null }> | null
    uuid: string
    loyalty?: { rewards?: Array<{ name: string }> } | null
}

interface IRestaurantCardProps {
    restaurant: IRestaurantCardRestaurant
    citySlug: string | undefined | null
    currency: ICurrency | undefined | null
    loadWithPriority: boolean
    styleType?: 'default' | 'lp-card' | 'app'
    index: number
    preventRouting?: boolean
    className?: string
}

export const RestaurantCard = ({
    restaurant,
    citySlug,
    currency,
    loadWithPriority,
    styleType = 'default',
    index,
    preventRouting = false,
    className,
}: IRestaurantCardProps) => {
    const locale = useLocale()

    const { imageHeight, imageWidth, firstImageContainerRef, allowedImageWidths, aspectRatio } =
        useRestaurantCardsContext()

    const [isLoading, setIsLoading] = useState(false)

    const onClick = useCallback((e: IOnClickEvent) => {
        // If click was made to open in new tab, don't call onClick
        if (e.ctrlKey || e.metaKey) return

        flushSync(() => {
            setIsLoading(true)
        })
    }, [])

    useEffect(() => {
        // Anchor tag causes problems with the loading state persisting, even after router back.
        setIsLoading(false)
    }, [])

    const image = restaurant.images?.[0]

    const [shouldUseOptimizedImage, setShouldUseOptimizedImage] = useState(false)

    const onImagePreload = useCallback(() => {
        setShouldUseOptimizedImage(true)
    }, [])

    usePreloadImage(
        image && imageHeight && imageWidth
            ? createImageUrl(image.url, {
                  height: imageHeight,
                  width: imageWidth,
                  resolution: 2,
              })
            : '',
        onImagePreload
    )

    const getImageWithResolution = useCallback(
        (imageUrl: string, resolution: number, loadSize: Record<'width' | 'height', number>) => {
            return createImageUrl(imageUrl, {
                height: loadSize.height,
                width: loadSize.width,
                resolution,
            })
        },
        []
    )

    const [oneXImageUrl, twoXImageUrl, { loadHeight, loadWidth }] = useMemo(() => {
        if (!image) return [null, null, { loadHeight: 0, loadWidth: 0 }]

        const loadWidth = (shouldUseOptimizedImage && imageWidth) || allowedImageWidths[0]

        const loadHeight =
            (shouldUseOptimizedImage && imageHeight) || Math.round(loadWidth / aspectRatio)

        return [
            getImageWithResolution(image.url, 1, { height: loadHeight, width: loadWidth }),
            getImageWithResolution(image.url, 2, { height: loadHeight, width: loadWidth }),
            { loadHeight, loadWidth },
        ]
    }, [
        image,
        imageWidth,
        imageHeight,
        shouldUseOptimizedImage,
        getImageWithResolution,
        aspectRatio,
        allowedImageWidths,
    ])

    const restaurantCardNode = run(() => {
        switch (styleType) {
            case 'default':
                return (
                    <div className={cn('flex flex-col', className)}>
                        <motion.div
                            ref={index === 0 ? firstImageContainerRef : undefined}
                            className="min-w-[100%] max-w-[100%] bg-grey-300 rounded-[16px] overflow-hidden flex items-center relative
                                z-[-1]"
                            style={{
                                aspectRatio: '16 / 9',
                            }}
                        >
                            {isLoading && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="absolute bg-black/50 w-full h-full flex items-center justify-center"
                                >
                                    <div>
                                        <LoadingSpinner size={40} color="white" />
                                    </div>
                                </motion.div>
                            )}

                            {restaurant.label === 'NEW' && (
                                <div
                                    className={`rounded-full w-fit capitalize px-2.5 min-h-6 max-h-7 text-[12px] flex items-center leading-0
                                        absolute top-3 left-3 font-semibold bg-black text-white`}
                                >
                                    <span>{restaurant.label}</span>
                                </div>
                            )}

                            {run(() => {
                                if (
                                    !image ||
                                    !oneXImageUrl ||
                                    !twoXImageUrl ||
                                    !loadHeight ||
                                    !loadWidth
                                ) {
                                    return null
                                }

                                return (
                                    <Image
                                        unoptimized
                                        priority={loadWithPriority}
                                        src={twoXImageUrl}
                                        alt={restaurant.name}
                                        width={loadWidth}
                                        height={loadHeight}
                                        className="object-cover w-full select-none"
                                        sizes={`1x ${oneXImageUrl}, 2x ${twoXImageUrl}`}
                                    />
                                )
                            })}
                        </motion.div>

                        <h4 className="font-semibold text-[18px] whitespace-nowrap overflow-hidden text-ellipsis mt-3 mb-0.5 text-black">
                            {restaurant.name}
                        </h4>

                        <div className="flex flex-row gap-x-2 text-[12px] font-medium items-center text-black/60">
                            {typeof restaurant.avgRating === 'number' &&
                                Boolean(restaurant.avgRating) && (
                                    <Fragment>
                                        <Rating
                                            withBrackets
                                            starsStyleType="minimal"
                                            rating={restaurant.avgRating}
                                            ratingCount={restaurant.ratingsCount}
                                            ratingCountStyleType="only-count"
                                            starClassName="w-3 h-3"
                                            colorClassName="text-black/60"
                                            starsClassName="top-[-1.5px]"
                                            ratingClassName="font-medium text-[12px]"
                                            labelClassName="font-medium text-[12px]"
                                            labelAndStarsContainerClassName="gap-x-1"
                                            containerClassName="gap-x-0.5"
                                        />

                                        <SeparationLine />
                                    </Fragment>
                                )}

                            <RestaurantTags
                                tags={restaurant.tags}
                                className="text-[12px] md:text-[12px]"
                            />

                            {currency && restaurant.tags && restaurant.tags.length > 0 && (
                                <SeparationLine />
                            )}

                            <RestaurantPriceRange
                                currencySymbol={currency?.symbol ?? null}
                                priceRange={restaurant.priceRange}
                                className="text-[inherit]"
                            />
                        </div>

                        <RestaurantCardDeals restaurant={restaurant} />
                    </div>
                )

            case 'app':
                return (
                    <div
                        className={cn(
                            'flex flex-1 flex-row items-center bg-white rounded-[16px] p-2 pr-0 w-full overflow-hidden max-w-full',
                            className
                        )}
                    >
                        <motion.div
                            ref={index === 0 ? firstImageContainerRef : undefined}
                            className="min-w-20 max-w-1 bg-grey-300 rounded-[16px] overflow-hidden flex items-center relative mr-3"
                            style={{
                                aspectRatio: '1 / 1',
                            }}
                        >
                            {isLoading && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="absolute bg-black/50 w-full h-full flex items-center justify-center"
                                >
                                    <div>
                                        <LoadingSpinner size={40} color="white" />
                                    </div>
                                </motion.div>
                            )}

                            {restaurant.label === 'NEW' && (
                                <div
                                    className={`rounded-full w-fit capitalize px-2.5 min-h-6 max-h-7 text-[12px] flex items-center leading-0
                                        absolute top-3 left-3 font-semibold bg-black text-white`}
                                >
                                    <span>{restaurant.label}</span>
                                </div>
                            )}

                            {run(() => {
                                if (
                                    !image ||
                                    !oneXImageUrl ||
                                    !twoXImageUrl ||
                                    !loadHeight ||
                                    !loadWidth
                                ) {
                                    return null
                                }

                                return (
                                    <Image
                                        unoptimized
                                        priority={loadWithPriority}
                                        src={twoXImageUrl}
                                        alt={restaurant.name}
                                        width={loadWidth}
                                        height={loadHeight}
                                        className="object-cover w-full select-none h-full"
                                        sizes={`1x ${oneXImageUrl}, 2x ${twoXImageUrl}`}
                                    />
                                )
                            })}
                        </motion.div>

                        <div
                            className="flex flex-col w-full max-w-full flex-1"
                            style={{ maxWidth: `calc(100% - 80px - 12px)` }}
                        >
                            <h4 className="font-semibold text-[16px] whitespace-nowrap overflow-hidden text-ellipsis mb-0.5 text-black pr-2">
                                {restaurant.name}
                            </h4>

                            <div className="flex flex-row gap-x-2 text-[12px] font-medium items-center text-black/60 w-full pr-2">
                                {typeof restaurant.avgRating === 'number' &&
                                    Boolean(restaurant.avgRating) && (
                                        <Fragment>
                                            <Rating
                                                withBrackets
                                                starsStyleType="minimal"
                                                rating={restaurant.avgRating}
                                                ratingCount={restaurant.ratingsCount}
                                                ratingCountStyleType="only-count"
                                                starClassName="w-3 h-3"
                                                colorClassName="text-black/60"
                                                starsClassName="top-[-1.5px]"
                                                ratingClassName="font-medium text-[12px]"
                                                labelClassName="font-medium text-[12px]"
                                                labelAndStarsContainerClassName="gap-x-1"
                                                containerClassName="gap-x-0.5"
                                            />

                                            <SeparationLine />
                                        </Fragment>
                                    )}

                                <RestaurantTags
                                    tags={restaurant.tags}
                                    className="text-[12px] md:text-[12px]"
                                />

                                {currency && restaurant.tags && restaurant.tags.length > 0 && (
                                    <SeparationLine />
                                )}

                                <RestaurantPriceRange
                                    currencySymbol={currency?.symbol ?? null}
                                    priceRange={restaurant.priceRange}
                                    className="text-inherit"
                                    filledColorClassName="text-black/60 font-normal"
                                    emptyColorClassName="text-black/20 font-normal"
                                />
                            </div>

                            <RestaurantCardDeals restaurant={restaurant} />
                        </div>
                    </div>
                )

            case 'lp-card':
                return (
                    <div
                        ref={index === 0 ? firstImageContainerRef : undefined}
                        className={cn(
                            'rounded-[6px] min-w-[310px] max-w-[80vw] w-max sm:min-w-[476px] sm:max-w-[476px] bg-white relative overflow-hidden',
                            className
                        )}
                        style={{ boxShadow: '0 0.5rem 1.5rem rgba(28, 29, 40, 0.1)' }}
                    >
                        {run(() => {
                            if (
                                !image ||
                                !oneXImageUrl ||
                                !twoXImageUrl ||
                                !loadHeight ||
                                !loadWidth
                            ) {
                                return null
                            }

                            return (
                                <Image
                                    unoptimized
                                    priority={loadWithPriority}
                                    src={twoXImageUrl}
                                    alt={restaurant.name}
                                    width={loadWidth}
                                    height={loadHeight}
                                    className="object-cover w-full select-none"
                                    sizes={`1x ${oneXImageUrl}, 2x ${twoXImageUrl}`}
                                />
                            )
                        })}

                        <div className="p-6 pt-0 relative mt-[-24px] z-[1]">
                            <h4
                                className="font-bold text-[22px] md:text-[31px] whitespace-nowrap overflow-hidden text-ellipsis mt-3 text-black
                                    z-[2] relative"
                            >
                                {restaurant.name}
                            </h4>

                            <FadeOverlay
                                className="absolute left-0 z-[1] origin-bottom scale-y-50 scale-x-[1.05]"
                                style={{ bottom: `calc(100% - 14px)` }}
                            />

                            <div className="flex flex-row gap-x-2 text-[12px] font-medium items-center text-black/60">
                                {typeof restaurant.avgRating === 'number' &&
                                    Boolean(restaurant.avgRating) &&
                                    restaurant.ratingsCount > 0 && (
                                        <Fragment>
                                            <Rating
                                                withBrackets
                                                starsStyleType="minimal"
                                                rating={restaurant.avgRating}
                                                ratingCount={restaurant.ratingsCount}
                                                ratingCountStyleType="only-count"
                                                starClassName="w-3 h-3"
                                                colorClassName="text-black/60"
                                                starsClassName="top-[-1.5px]"
                                                ratingClassName="font-medium text-[12px]"
                                                labelClassName="font-medium text-[12px]"
                                                labelAndStarsContainerClassName="gap-x-1"
                                                containerClassName="gap-x-0.5"
                                            />

                                            <SeparationLine />
                                        </Fragment>
                                    )}

                                <RestaurantTags
                                    tags={restaurant.tags}
                                    className="text-[12px] md:text-[12px]"
                                />

                                {restaurant.priceRange && <SeparationLine />}

                                <RestaurantPriceRange
                                    currencySymbol={currency?.symbol ?? '€'}
                                    priceRange={restaurant.priceRange}
                                    className="text-[inherit]"
                                />
                            </div>

                            <RestaurantCardDeals
                                restaurant={restaurant}
                                className="items-start gap-y-1 mt-4"
                            />
                        </div>
                    </div>
                )
        }
    })

    if (citySlug && !preventRouting) {
        return (
            <Link
                onClick={onClick}
                href={getLinkHref(
                    {
                        pattern: '/restaurants/[citySlug]/[restaurantSlug]',
                        params: { citySlug, restaurantSlug: restaurant.slug },
                    },
                    locale as Locale
                )}
            >
                {restaurantCardNode}
            </Link>
        )
    }

    return restaurantCardNode
}
